import type { ApiErrorBody } from '@hearth/shared';

/** Error thrown by every `api.*` call for non-2xx responses and network failures. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type RequestOptions = {
  signal?: AbortSignal;
};

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type UnauthorizedHandler = (nextPath: string) => void;

/** Header the API requires on every state-changing request (custom-header CSRF guard). */
export const CSRF_HEADER = 'X-Hearth-CSRF';

const AUTH_PATH_PREFIX = '/api/auth/';

let unauthorizedHandler: UnauthorizedHandler = (nextPath) => {
  window.location.assign(`/login?next=${encodeURIComponent(nextPath)}`);
};

/**
 * Replaces what happens when a non-auth request returns 401 (session expired). The app shell installs a
 * handler that clears the cached user and navigates client-side; the default does a full-page redirect.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler;
}

function currentPath(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // Non-JSON error page (proxy/gateway); fall through to a generic message.
  }
  const error = (body as Partial<ApiErrorBody> | null)?.error;
  if (error && typeof error.message === 'string') {
    return new ApiError(response.status, error.code ?? 'error', error.message, error.details);
  }
  return new ApiError(
    response.status,
    'http_error',
    response.status >= 500 ? 'The server had a problem. Please try again.' : `Request failed (${response.status})`,
  );
}

async function request<T>(method: Method, path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: BodyInit | undefined;
  if (method !== 'GET') headers[CSRF_HEADER] = '1';
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body: payload,
      credentials: 'same-origin',
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'network_error', 'Could not reach the server. Check your connection and try again.');
  }

  if (!response.ok) {
    const error = await parseError(response);
    if (response.status === 401 && !path.startsWith(AUTH_PATH_PREFIX) && !window.location.pathname.startsWith('/login')) {
      unauthorizedHandler(currentPath());
    }
    throw error;
  }
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

/**
 * Thin JSON client for the Hearth API. Same-origin cookies carry the session; non-GET requests send the
 * CSRF header. Pass a `FormData` body for file uploads.
 */
export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body, options),
  delete: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('DELETE', path, body, options),
};

export type QueryValue = string | number | boolean | Date | null | undefined | readonly (string | number)[];

/**
 * Builds `path?query` in the encoding the API's list schemas accept: arrays become comma-separated values;
 * `undefined`, `null`, empty strings and empty arrays are omitted; dates become ISO strings.
 */
export function apiUrl(path: string, query: Record<string, QueryValue> = {}): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length > 0) params.set(key, value.join(','));
    } else if (value instanceof Date) {
      params.set(key, value.toISOString());
    } else {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * Maps a validation error's details (`[{ path, message }]`, dot-joined paths) to `{ [path]: message }`.
 * The first message per path wins. Returns `{}` for any other error.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  const result: Record<string, string> = {};
  if (!(error instanceof ApiError) || !Array.isArray(error.details)) return result;
  for (const detail of error.details as unknown[]) {
    if (typeof detail !== 'object' || detail === null) continue;
    const { path, message } = detail as { path?: unknown; message?: unknown };
    if (typeof path === 'string' && typeof message === 'string' && !(path in result)) result[path] = message;
  }
  return result;
}

/** Human message for any thrown value — server message for ApiError. */
export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}
